import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Form, Row, Col, Card, Button, Spinner, Alert } from 'react-bootstrap';
import PageHeader from '../components/PageHeader';
import { adminApi, extractError } from '../api/client';
import { useToast } from '../contexts/ToastContext';
import { SECTION_ORDER, SECTION_CONFIGS } from '../lib/countySections';

const EMPTY = {
  name: '',
  slug: '',
  county_code: '',
  official_election_website: '',
  notes: '',
  status: 'active',
};

export default function CountyFormPage({ mode }) {
  const navigate = useNavigate();
  const { stateId, countyId } = useParams();
  const toast = useToast();
  const [state, setState] = useState(null);
  const [data, setData] = useState(EMPTY);
  const [original, setOriginal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [errors, setErrors] = useState({});
  const [summary, setSummary] = useState(null);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const promises = [adminApi.get(`/admin/states/${stateId}`)];
        if (mode === 'edit') promises.push(adminApi.get(`/admin/states/${stateId}/counties/${countyId}`));
        if (mode === 'edit') promises.push(adminApi.get(`/admin/states/${stateId}/counties/${countyId}/summary`));
        const responses = await Promise.all(promises);
        if (!active) return;
        setState(responses[0].data);
        if (mode === 'edit') {
          setData(responses[1].data);
          setOriginal(responses[1].data);
          setSummary(responses[2]?.data || null);
        }
      } catch (e) {
        if (active) setError(extractError(e));
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [stateId, countyId, mode]);

  function update(field, value) {
    setData((d) => ({ ...d, [field]: value }));
  }

  function validate() {
    const errs = {};
    if (!data.name?.trim()) errs.name = 'Required';
    if (data.official_election_website && !/^https?:\/\//i.test(data.official_election_website)) {
      errs.official_election_website = 'Must start with http(s)://';
    }
    setErrors(errs);
    return Object.keys(errs).length === 0;
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');
    if (!validate()) {
      setError('Please correct the highlighted fields.');
      return;
    }
    setSaving(true);
    try {
      if (mode === 'create') {
        const res = await adminApi.post(`/admin/states/${stateId}/counties`, data);
        toast.success('County created');
        navigate(`/admin/states/${stateId}/counties/${res.data.id}`);
      } else {
        const res = await adminApi.put(`/admin/states/${stateId}/counties/${countyId}`, data);
        setData(res.data);
        setOriginal(res.data);
        toast.success('County updated');
      }
    } catch (e) {
      const msg = extractError(e);
      setError(msg);
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return (
      <>
        <PageHeader title={mode === 'create' ? 'New county' : 'Edit county'} />
        <div className="text-center p-5"><Spinner animation="border" /></div>
      </>
    );
  }

  return (
    <>
      <PageHeader
        title={mode === 'create' ? 'Add county' : (original?.name || 'Edit county')}
        breadcrumb={
          <span>
            <Link to="/admin/states" className="text-decoration-none">States</Link>
            {state && <> / <Link to={`/admin/states/${stateId}`} className="text-decoration-none">{state.name}</Link></>}
            {' / '} <Link to={`/admin/states/${stateId}/counties`} className="text-decoration-none">Counties</Link>
            {' / '} {mode === 'create' ? 'New' : (original?.name || 'Edit')}
          </span>
        }
        actions={
          <div className="d-flex gap-2">
            <Button variant="outline-secondary" onClick={() => navigate(`/admin/states/${stateId}/counties`)}>Cancel</Button>
            <Button variant="primary" onClick={handleSubmit} disabled={saving} data-testid="county-form-save-button">
              {saving ? <Spinner size="sm" animation="border" /> : (mode === 'create' ? 'Create county' : 'Save changes')}
            </Button>
          </div>
        }
      />
      <div className="page-content">
        {mode === 'edit' && summary && (
          <Card className="mb-3">
            <Card.Body className="d-flex flex-wrap gap-2 align-items-center">
              <span className="text-muted me-2" style={{ fontSize: '0.85rem', fontWeight: 600 }}>County data:</span>
              {SECTION_ORDER.map((slug) => {
                const cfg = SECTION_CONFIGS[slug];
                const counts = summary?.[
                  slug === 'election-dates' ? 'election_dates'
                  : slug === 'supervisors' ? 'supervisors'
                  : slug === 'party-organizations' ? 'party_organizations'
                  : 'local_groups'
                ] || { total: 0, published: 0 };
                return (
                  <Link key={slug} to={`/admin/states/${stateId}/counties/${countyId}/${slug}`} className="filter-chip text-decoration-none" data-testid={`county-form-section-${slug}`}>
                    <i className={`bi ${cfg.icon}`} aria-hidden="true" />
                    <span className="ms-1">{cfg.titlePlural}</span>
                    <span className="badge bg-light text-dark border ms-2" style={{ fontSize: '0.7rem' }}>{counts.total}</span>
                  </Link>
                );
              })}
            </Card.Body>
          </Card>
        )}
        {error && <Alert variant="danger">{error}</Alert>}
        <Form noValidate onSubmit={handleSubmit}>
          <Row className="g-3">
            <Col xs={12} lg={8}>
              <Card>
                <Card.Body>
                  <Row className="g-3">
                    <Col xs={12} md={8}>
                      <Form.Group>
                        <Form.Label>County name <span className="text-danger">*</span></Form.Label>
                        <Form.Control isInvalid={!!errors.name} value={data.name || ''} onChange={(e) => update('name', e.target.value)} placeholder="e.g., Miami-Dade" data-testid="county-name-input" />
                        <Form.Control.Feedback type="invalid">{errors.name}</Form.Control.Feedback>
                      </Form.Group>
                    </Col>
                    <Col xs={6} md={4}>
                      <Form.Group>
                        <Form.Label>County code (FIPS)</Form.Label>
                        <Form.Control value={data.county_code || ''} onChange={(e) => update('county_code', e.target.value)} placeholder="e.g., 086" data-testid="county-code-input" />
                      </Form.Group>
                    </Col>
                    <Col xs={12} md={6}>
                      <Form.Group>
                        <Form.Label>Slug</Form.Label>
                        <Form.Control value={data.slug || ''} onChange={(e) => update('slug', e.target.value)} placeholder="auto-generated from name" data-testid="county-slug-input" />
                        <Form.Text>Unique within this state. Auto-generated if blank.</Form.Text>
                      </Form.Group>
                    </Col>
                    <Col xs={12} md={6}>
                      <Form.Group>
                        <Form.Label>Status</Form.Label>
                        <Form.Select value={data.status || 'active'} onChange={(e) => update('status', e.target.value)} data-testid="county-status-select">
                          <option value="active">Active</option>
                          <option value="inactive">Inactive</option>
                        </Form.Select>
                      </Form.Group>
                    </Col>
                    <Col xs={12}>
                      <Form.Group>
                        <Form.Label>Official election website</Form.Label>
                        <Form.Control isInvalid={!!errors.official_election_website} value={data.official_election_website || ''} onChange={(e) => update('official_election_website', e.target.value)} placeholder="https://…" data-testid="county-website-input" />
                        <Form.Control.Feedback type="invalid">{errors.official_election_website}</Form.Control.Feedback>
                      </Form.Group>
                    </Col>
                    <Col xs={12}>
                      <Form.Group>
                        <Form.Label>Notes</Form.Label>
                        <Form.Control as="textarea" rows={4} value={data.notes || ''} onChange={(e) => update('notes', e.target.value)} placeholder="Polling locations, supervisor of elections, contact info, etc." data-testid="county-notes-input" />
                      </Form.Group>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
            </Col>
            <Col xs={12} lg={4}>
              <Card>
                <Card.Header>State</Card.Header>
                <Card.Body>
                  {state ? (
                    <>
                      <div className="fw-semibold">{state.name}</div>
                      <div className="text-muted" style={{ fontSize: '0.85rem' }}>{state.abbreviation} · {state.counties_count ?? '—'} counties total</div>
                      {state.official_election_website && (
                        <div className="mt-2" style={{ fontSize: '0.85rem' }}>
                          <i className="bi bi-link-45deg me-1" />
                          <a href={state.official_election_website} target="_blank" rel="noreferrer">State elections site</a>
                        </div>
                      )}
                    </>
                  ) : <span className="text-muted">—</span>}
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Form>
      </div>
    </>
  );
}
